import { CONFIG_SERVER, MIME_TO_EXTENSION } from '@vindr/services/constants/common.constant';
import keycloak from '@vindr/services/keycloak';
import { stopReload } from '@vindr/utils/helpers/common.helper';
import { Dispatch } from 'react';
import { clearListCache } from '../../Webcam/helper/storeIndexDB';
import {
  CaptureManagerAction,
  CaptureManagerContextProps,
  CaptureManagerProps,
} from '../context/type';
import S3Uploader, { S3UploaderConfig } from '../../../Upload/S3Uploader';
import { cleanDiacritics, uuidv4 } from '@vindr/utils/helpers';
import pLimit from 'p-limit';
import { getExtensionFileName } from '@vindr/utils/converter';
const limit = pLimit(6);

const uploadFiles = async ({
  params,
  actionUpload,
  selected,
  storeFiles,
  dispatch,
  processingList,
}: {
  params: CaptureManagerProps['params'];
  actionUpload: CaptureManagerProps['actionUpload'];
  selected: CaptureManagerContextProps['selected'];
  storeFiles: CaptureManagerContextProps['storeFiles'];
  dispatch: Dispatch<CaptureManagerAction>;
  processingList: CaptureManagerContextProps['processingList'];
}): Promise<false | true> => {
  let currentParams = params;
  if (actionUpload && !currentParams) {
    try {
      const response = await actionUpload?.();
      if (!response) return false;

      currentParams = response;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  window.addEventListener('beforeunload', stopReload, { capture: true });

  if (!selected?.length) return;

  //? Only re-upload ready and exception file
  const selectedValidList = selected
    .map((it) => {
      return storeFiles.find((f) => f.id === it.id);
    })
    .filter((file) => {
      const process = processingList.get(file.id);

      return process?.status !== 'success';
    });

  const filesToUpload = selectedValidList
    .filter((it) => !!it)
    .map((it) => ({ ...it, name: `${uuidv4()}_${cleanDiacritics(it.name).replaceAll(' ', '_')}` }));
  if (!filesToUpload?.length) return;

  try {
    const requestOptions = {
      method: 'POST',
      body: JSON.stringify({
        file_names: filesToUpload.map((it) => it.name),
        action: 'UPLOAD',
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      },
    };

    const presignUrlData = await fetch(
      CONFIG_SERVER.BACKEND_URL + '/v1/attachment/upload/url',
      requestOptions
    ).then((response) => response?.json?.());
    const presignUrls = presignUrlData?.data?.presigned_object;
    if (!presignUrls) return false;

    const metaOptions = {
      method: 'POST',
      body: JSON.stringify({
        ...currentParams,
        study_description: Array.isArray(currentParams?.study_description)
          ? currentParams?.study_description?.join(',')
          : currentParams?.study_description,
        file_names: filesToUpload.map((it) => it.name),
        action: 'UPLOAD',
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      },
    };
    const status = await fetch(
      CONFIG_SERVER.BACKEND_URL + '/v1/attachment/upload-file-metadata',
      metaOptions
    ).then((response) => response.status);
    if (status !== 200) return false;

    const splitLimitSize = filesToUpload.map((it) => [
      {
        ...it,
        presignUrl: presignUrls.find((urlIt) => urlIt.file_name === it.name)?.url,
      },
    ]);

    Promise.all(
      splitLimitSize.map((chunkFile) =>
        limit(() =>
          onUploading({
            chunk: chunkFile,
            dispatch,
          })
        )
      )
    ).then(async () => {
      //? Clear list cache after upload done
      const listKey = selectedValidList.flatMap((file) => [
        file?.name,
        `backup-${file?.id}`,
        file?.id,
      ]);

      await clearListCache(listKey);

      window.removeEventListener('beforeunload', stopReload, { capture: true });
    });

    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
};

function onUploading({ chunk, dispatch }) {
  let dataToSend = null;
  const uploaderConfig: S3UploaderConfig = {};
  let uploader = null;

  // S3 upload need presignUrl
  const file = chunk[0];
  uploaderConfig.url = file.presignUrl;
  const mimeType = MIME_TO_EXTENSION[getExtensionFileName(file.name)];
  if (mimeType) {
    uploaderConfig.headers = {
      'Content-Type': mimeType,
    };
  }
  uploader = new S3Uploader(uploaderConfig);
  dataToSend = file.file;

  const chunksID = chunk.map((file) => file.id);

  dispatch({ type: 'setAbort', payload: uploader.abort.bind(uploader) });

  uploader.onProgress = ({ status, percent }) => {
    let stateStatus = 'uploading';

    if (typeof status === 'number') {
      stateStatus = status === 200 ? 'success' : 'exception';
    }

    dispatch({
      type: 'SET_PROGRESS',
      payload: { id: chunksID, progress: percent, status: stateStatus },
    });
  };

  uploader.end = () => {
    dispatch({
      type: 'SET_PROGRESS',
      payload: { id: chunksID, progress: 100, status: 'success' },
    });
    console.log('SUCCESS upload chunk ', chunksID);
  };

  uploader.error = (error) => {
    if (!error) return;

    console.log('ERROR upload chunk ', chunksID);
    dispatch({
      type: 'SET_PROGRESS',
      payload: {
        id: chunksID,
        progress: 0,
        status: 'exception',
      },
    });
    return error;
  };

  return uploader.send(dataToSend);
}

export default uploadFiles;
